import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';
interface FormData {
  interest: string;
  fullName: string;
  phone: string;
  email: string;
}
interface AppointmentSchedulerProps {
  formData: FormData;
}
const LEADCONNECTOR_WEBHOOK = 'https://services.leadconnectorhq.com/hooks/9bw9Tk4GstO34KNKeVbp/webhook-trigger/8b1640ae-2b70-46f8-92d5-f359f32a0fa6';
type Step = 'date' | 'loading' | 'time' | 'confirmed';
const ALL_TIME_SLOTS = ['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'];
const MAKE_WEBHOOK_URL = 'https://hook.us2.make.com/55u22nbpdbpcp4gxjfbrdb45hux2u2d3';
const POLL_INTERVAL = 45000; // 45 seconds
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function formatDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
async function fetchAvailableSlots(date: Date): Promise<string[]> {
  try {
    const res = await fetch(MAKE_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'get_availability',
        date: formatDateISO(date)
      })
    });
    if (!res.ok) throw new Error('Webhook error');
    const data = await res.json();
    // Expected response: { "available_slots": ["9:00 AM", "10:30 AM", ...] }
    if (data?.available_slots && Array.isArray(data.available_slots)) {
      return data.available_slots;
    }
    // Fallback: if Make returns all slots or unexpected format, show all
    return ALL_TIME_SLOTS;
  } catch {
    // On error, fall back to showing all slots so the user isn't blocked
    return ALL_TIME_SLOTS;
  }
}
export function AppointmentScheduler({
  formData
}: AppointmentSchedulerProps) {
  const fullName = formData.fullName || 'Guest';
  const [step, setStep] = useState<Step>('date');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<string[]>(ALL_TIME_SLOTS);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Fetch slots for a given date
  const loadSlots = useCallback(async (date: Date) => {
    setSlotsLoading(true);
    const slots = await fetchAvailableSlots(date);
    setAvailableSlots(slots);
    setSlotsLoading(false);
  }, []);
  // Start polling when on the time step
  useEffect(() => {
    if (step === 'time' && selectedDate) {
      // Initial fetch
      loadSlots(selectedDate);
      // Poll every 45s
      pollRef.current = setInterval(() => {
        loadSlots(selectedDate);
      }, POLL_INTERVAL);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [step, selectedDate, loadSlots]);
  // Calendar helpers
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (prev >= new Date(today.getFullYear(), today.getMonth(), 1)) {
      setCurrentMonth(prev);
    }
  };
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep('time');
  };
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };
  const confirmAppointment = async () => {
    if (selectedDate && selectedTime) {
      // Stop polling
      if (pollRef.current) clearInterval(pollRef.current);
      setStep('confirmed');
      // Send complete lead + appointment data to LeadConnector
      try {
        const params = new URLSearchParams();
        params.append('interest', formData.interest);
        params.append('full_name', formData.fullName);
        params.append('phone', formData.phone);
        params.append('email', formData.email);
        params.append('appointment_date', formatDateISO(selectedDate));
        params.append('appointment_date_formatted', formatDate(selectedDate));
        params.append('appointment_time', selectedTime);
        params.append('appointment_time_end', getEndTime(selectedTime));
        await fetch(LEADCONNECTOR_WEBHOOK, {
          method: 'POST',
          body: params
        });
      } catch {
        // Silently fail — user already sees confirmation
      }
    }
  };
  const goBackToDate = () => {
    setSelectedTime(null);
    if (pollRef.current) clearInterval(pollRef.current);
    setStep('date');
  };
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10 w-10" data-id="element-11"></div>);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isPast = date < today;
      const isDisabled = isWeekend || isPast;
      const isToday = date.getTime() === today.getTime();
      const isSelected = selectedDate?.getTime() === date.getTime();
      days.push(<button key={i} disabled={isDisabled} onClick={() => handleDateSelect(date)} className={`h-10 w-10 rounded-full flex items-center justify-center text-sm transition-all
            ${isDisabled ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-slate-100 text-brand-dark'}
            ${isToday && !isSelected ? 'border border-brand-dark font-medium' : ''}
            ${isSelected ? 'bg-brand-dark text-white hover:bg-brand-dark shadow-md' : ''}
          `} data-id="element-12">
          {i}
        </button>);
    }
    return days;
  };
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getEndTime = (startTime: string) => {
    const [time, period] = startTime.split(' ');
    let [hours, mins] = time.split(':').map(Number);
    mins += 30;
    if (mins >= 60) {
      mins -= 60;
      hours += 1;
      if (hours === 12 && period === 'AM') {
        // AM to PM
      } else if (hours > 12) {
        hours -= 12;
      }
    }
    const newMins = mins === 0 ? '00' : '30';
    return `${hours}:${newMins} ${period}`;
  };
  return <div className="w-full min-h-[400px] flex flex-col relative" data-id="element-13">
      <AnimatePresence mode="wait" data-id="element-14">
        {/* STEP 1: DATE SELECTION */}
        {step === 'date' && <motion.div key="date" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.3
      }} className="flex-grow flex flex-col" data-id="element-15">
            <div className="text-center mb-6" data-id="element-16">
              <h3 className="text-2xl font-light text-brand-dark mb-2" data-id="element-17">
                Schedule your visit
              </h3>
              <p className="text-brand-muted text-sm font-light" data-id="element-18">
                Select a preferred date for your appointment.
              </p>
            </div>

            <div className="border border-slate-200 rounded-2xl p-5 bg-white" data-id="element-19">
              <div className="flex items-center justify-between mb-6" data-id="element-20">
                <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-brand-dark disabled:opacity-30 disabled:hover:bg-transparent" disabled={currentMonth <= new Date(today.getFullYear(), today.getMonth(), 1)} data-id="element-21">
                  <ChevronLeft className="w-5 h-5" data-id="element-22" />
                </button>
                <h4 className="font-medium text-brand-dark" data-id="element-23">
                  {currentMonth.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
                </h4>
                <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-brand-dark" data-id="element-24">
                  <ChevronRight className="w-5 h-5" data-id="element-25" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-26">
                {DAYS_OF_WEEK.map(day => <div key={day} className="text-center text-xs font-medium text-slate-400 py-1" data-id="element-27">
                    {day}
                  </div>)}
              </div>

              <div className="grid grid-cols-7 gap-1 justify-items-center" data-id="element-28">
                {renderCalendar()}
              </div>
            </div>

            <p className="text-xs text-center text-slate-400 mt-4 font-light" data-id="element-29">
              Appointments available Monday – Friday.
            </p>
          </motion.div>}

        {/* STEP 2: TIME SELECTION */}
        {step === 'time' && selectedDate && <motion.div key="time" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 20
      }} transition={{
        duration: 0.3
      }} className="flex-grow flex flex-col" data-id="element-30">
            <button onClick={goBackToDate} className="flex items-center text-sm text-brand-muted hover:text-brand-dark transition-colors mb-4 w-fit" data-id="element-31">
              <ArrowLeft className="w-4 h-4 mr-1" data-id="element-32" /> Back to calendar
            </button>

            <div className="mb-6" data-id="element-33">
              <h3 className="text-2xl font-light text-brand-dark mb-2" data-id="element-34">
                Select a time
              </h3>
              <div className="flex items-center text-brand-dark font-medium bg-slate-50 px-4 py-2 rounded-lg border border-slate-100 w-fit" data-id="element-35">
                <Calendar className="w-4 h-4 mr-2 text-brand-muted" data-id="element-36" />
                {formatDate(selectedDate)}
              </div>
            </div>

            {slotsLoading && availableSlots.length === 0 /* Full loading state on first load */ ? <div className="flex-grow flex flex-col items-center justify-center py-12" data-id="element-37">
                <Loader2 className="w-8 h-8 text-brand-dark animate-spin mb-4" data-id="element-38" />
                <p className="text-brand-muted text-sm font-light" data-id="element-39">
                  Checking availability...
                </p>
              </div> : availableSlots.length === 0 /* No slots available */ ? <div className="flex-grow flex flex-col items-center justify-center py-12" data-id="element-40">
                <Calendar className="w-8 h-8 text-slate-300 mb-4" data-id="element-41" />
                <p className="text-brand-dark font-medium mb-1" data-id="element-42">
                  No availability
                </p>
                <p className="text-brand-muted text-sm font-light" data-id="element-43">
                  Please select a different date.
                </p>
                <button onClick={goBackToDate} className="mt-4 text-sm text-brand-dark underline underline-offset-2" data-id="element-44">
                  Choose another date
                </button>
              </div> : <>
                <div className="relative" data-id="element-45">
                  {/* Subtle refresh indicator */}
                  {slotsLoading && <div className="absolute -top-1 right-0 flex items-center text-xs text-brand-muted" data-id="element-46">
                      <Loader2 className="w-3 h-3 animate-spin mr-1" data-id="element-47" />
                      Updating...
                    </div>}
                  <div className="grid grid-cols-3 gap-3 mb-8 max-h-[240px] overflow-y-auto pr-2 custom-scrollbar" data-id="element-48">
                    {availableSlots.map(time => <button key={time} onClick={() => handleTimeSelect(time)} className={`py-2.5 px-2 rounded-xl text-sm transition-all border
                          ${selectedTime === time ? 'bg-brand-dark border-brand-dark text-white shadow-md' : 'bg-white border-slate-200 text-brand-dark hover:border-brand-dark/50 hover:bg-slate-50'}
                        `} data-id="element-49">
                        {time}
                      </button>)}
                  </div>
                </div>

                <div className="mt-auto pt-4" data-id="element-50">
                  <button onClick={confirmAppointment} disabled={!selectedTime} className="w-full bg-brand-dark hover:bg-brand-dark/90 text-white font-medium py-4 px-6 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-51">
                    Confirm Appointment
                  </button>
                </div>
              </>}
          </motion.div>}

        {/* STEP 3: CONFIRMATION */}
        {step === 'confirmed' && selectedDate && selectedTime && <motion.div key="confirmed" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} className="flex-grow flex flex-col items-center justify-center text-center py-8" data-id="element-52">
            <div className="relative w-20 h-20 mb-6 flex items-center justify-center" data-id="element-53">
              <motion.div className="absolute inset-0 rounded-full bg-green-200/40" initial={{
            scale: 0.8,
            opacity: 0.8
          }} animate={{
            scale: 2,
            opacity: 0
          }} transition={{
            duration: 1.5,
            ease: 'easeOut',
            repeat: Infinity,
            repeatDelay: 0.5
          }} data-id="element-54" />
              <motion.div className="absolute inset-0 rounded-full bg-green-200/30" initial={{
            scale: 0.8,
            opacity: 0.6
          }} animate={{
            scale: 1.6,
            opacity: 0
          }} transition={{
            duration: 1.5,
            ease: 'easeOut',
            delay: 0.3,
            repeat: Infinity,
            repeatDelay: 0.5
          }} data-id="element-55" />
              <motion.div className="relative w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center" initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 12,
            delay: 0.1
          }} data-id="element-56">
                <motion.div initial={{
              scale: 0,
              rotate: -45
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              stiffness: 250,
              damping: 15,
              delay: 0.3
            }} data-id="element-57">
                  <CheckCircle2 className="w-10 h-10" data-id="element-58" />
                </motion.div>
              </motion.div>
            </div>

            <h3 className="text-3xl font-light text-brand-dark mb-2" data-id="element-59">
              You're all set!
            </h3>
            <p className="text-brand-muted font-light mb-8" data-id="element-60">
              Thanks {fullName.split(' ')[0]}, we've received your request.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 w-full text-left mb-8" data-id="element-61">
              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4" data-id="element-62">
                Appointment Details
              </h4>
              <div className="space-y-4" data-id="element-63">
                <div className="flex items-start" data-id="element-64">
                  <Calendar className="w-5 h-5 text-brand-dark mt-0.5 mr-3 flex-shrink-0" data-id="element-65" />
                  <div data-id="element-66">
                    <p className="text-sm text-slate-500" data-id="element-67">Date</p>
                    <p className="font-medium text-brand-dark" data-id="element-68">
                      {formatDate(selectedDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-69">
                  <Clock className="w-5 h-5 text-brand-dark mt-0.5 mr-3 flex-shrink-0" data-id="element-70" />
                  <div data-id="element-71">
                    <p className="text-sm text-slate-500" data-id="element-72">Time</p>
                    <p className="font-medium text-brand-dark" data-id="element-73">
                      {selectedTime} – {getEndTime(selectedTime)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-brand-muted font-light" data-id="element-74">
              Our team will confirm your appointment via phone or email shortly.
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}